import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, CalendarOff } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog, Field, FormRow, Switch, EmptyState, ErrorState, TableSkeleton, fmtDate } from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, useStableCallback, IconButton, toDay } from './hrKit';

const DAYS = [
  { key: 'monday', label: 'Monday' }, { key: 'tuesday', label: 'Tuesday' }, { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' }, { key: 'friday', label: 'Friday' }, { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];
const DEFAULT = { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false };
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const showDate = (v) => (v ? fmtDate(String(v).slice(0, 10)) : '—');

function UnavailabilityDialog({ open, staffId, onSaved, onClose }) {
  const [form, setForm] = useState({ startDate: '', endDate: '', reason: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm({ startDate: '', endDate: '', reason: '' }); setErrors({}); } }, [open]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });

  async function save(e) {
    e.preventDefault();
    const errs = {};
    if (!form.startDate) errs.startDate = 'Choose the first day.';
    if (!form.endDate) errs.endDate = 'Choose the last day.';
    else if (form.startDate && form.endDate < form.startDate) errs.endDate = 'The last day is before the first day.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.post('/staff/unavailability', { staffId, startDate: form.startDate, endDate: form.endDate, reason: form.reason, allDay: true });
      toast.success('Unavailable period added');
      setSaving(false); onSaved(); onClose();
    } catch (err) { setErrors({ form: err.response?.data?.message || "It wasn't saved. Try again." }); setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={close} size="sm" title="Add an unavailable period"
      description="Days they can't be rostered, such as a holiday or study block."
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="unavail-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add period'}</button>
        </>
      )}>
      <form id="unavail-form" onSubmit={save} className="space-y-4">
        <FormRow>
          <Field label="From" required error={errors.startDate}>{(id) => <input id={id} type="date" className="input-field" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />}</Field>
          <Field label="To" required error={errors.endDate}>{(id) => <input id={id} type="date" className="input-field" min={form.startDate || undefined} value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />}</Field>
        </FormRow>
        <Field label="Reason">{(id) => <input id={id} className="input-field" value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="e.g. Holiday" />}</Field>
      </form>
    </Dialog>
  );
}

export default function StaffAvailability() {
  const { id } = useParams();
  const confirm = useConfirm();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [avail, setAvail] = useState(DEFAULT);
  const saved = useRef(JSON.stringify(DEFAULT));
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    Promise.all([api.get(`/staff/availability/${id}`), api.get(`/staff/unavailability/${id}`)])
      .then(([a, u]) => {
        const next = { ...DEFAULT };
        if (a.data && typeof a.data === 'object') DAYS.forEach(({ key }) => { if (a.data[key] !== undefined) next[key] = !!a.data[key]; });
        saved.current = JSON.stringify(next);
        setAvail(next);
        setPeriods(Array.isArray(u.data) ? u.data : []);
      })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load availability. Try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const dirty = JSON.stringify(avail) !== saved.current;

  async function saveAvailability() {
    setSaving(true);
    try {
      await api.post('/staff/availability', { staffId: id, ...avail });
      saved.current = JSON.stringify(avail);
      toast.success('Work days saved');
    } catch (e) { toast.error(e.response?.data?.message || "The work days weren't saved. Try again."); }
    finally { setSaving(false); }
  }

  async function deletePeriod(p) {
    if (!await confirm(`${showDate(p.start_date)} – ${showDate(p.end_date)} will be removed, and they can be rostered on those days again.`, { title: 'Remove unavailable period?', confirmLabel: 'Remove', danger: true })) return;
    try {
      await api.delete(`/staff/unavailability/${id}/${p.id}`);
      toast.success('Period removed');
      setPeriods((list) => list.filter((x) => x.id !== p.id));
    } catch (e) { toast.error(e.response?.data?.message || "The period wasn't removed. Try again."); }
  }

  const today = todayKey();
  const sorted = [...periods].sort((a, b) => (toDay(a.start_date) || 0) - (toDay(b.start_date) || 0));

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      {loading ? <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={5} cols={2} /></div>
        : loadError ? <div className="rounded-xl border border-slate-200 bg-white"><ErrorState message={loadError} onRetry={load} /></div>
          : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <section aria-labelledby="workdays-h" className="rounded-xl border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
                  <div>
                    <h2 id="workdays-h" className="text-[15px] font-semibold text-slate-900">Work days</h2>
                    <p className="text-[12.5px] text-slate-500">The days of the week they can be rostered.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {dirty && !saving && <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-amber-700"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />Unsaved changes</span>}
                    <button type="button" onClick={saveAvailability} disabled={saving || !dirty} className={dirty ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}>{saving ? 'Saving…' : 'Save work days'}</button>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {DAYS.map(({ key, label }) => (
                    <li key={key} className="flex items-center justify-between gap-3 px-5 py-2.5">
                      <span className="text-sm font-medium text-slate-800">{label}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-[12.5px] text-slate-500">{avail[key] ? 'Available' : 'Not available'}</span>
                        <Switch checked={!!avail[key]} onChange={(v) => setAvail((a) => ({ ...a, [key]: v }))} label={`Available on ${label}`} />
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              <section aria-labelledby="unavail-h" className="rounded-xl border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
                  <div>
                    <h2 id="unavail-h" className="text-[15px] font-semibold text-slate-900">Unavailable periods</h2>
                    <p className="text-[12.5px] text-slate-500">Specific dates they can't work.</p>
                  </div>
                  <button type="button" onClick={() => setDialogOpen(true)} className="btn-secondary btn-sm"><Plus size={13} aria-hidden="true" />Add period</button>
                </div>
                {sorted.length === 0 ? <EmptyState icon={CalendarOff} title="No unavailable periods" hint="Add holidays and other dates they can't be rostered." /> : (
                  <ul className="divide-y divide-slate-100">
                    {sorted.map((p) => {
                      const past = String(p.end_date || '').slice(0, 10) < today;
                      return (
                        <li key={p.id} className="flex items-center gap-3 px-5 py-2.5">
                          <div className="min-w-0 flex-1">
                            <p className={past ? 'text-sm text-slate-500' : 'text-sm font-medium text-slate-800'}>{showDate(p.start_date)} – {showDate(p.end_date)}{past && <span className="ml-2 text-[12px] text-slate-500">Past</span>}</p>
                            {p.reason && <p className="text-[12.5px] text-slate-500">{p.reason}</p>}
                          </div>
                          <IconButton label={`Remove ${showDate(p.start_date)} to ${showDate(p.end_date)}`} icon={Trash2} danger onClick={() => deletePeriod(p)} />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          )}
      <UnavailabilityDialog open={dialogOpen} staffId={id} onSaved={load} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
